import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Grid from 'apollo-react/components/Grid';
import MultiFormSubmission from '../MultiFormSubmission/MultiFormSubmission';
import RequestAuditTrails from '../../../common/RequestAuditTrails';
import AdditionalDocuments from '../../../common/AdditionalDocuments';
import LinkedStudiesSelector from '../../../common/LinkedStudies/LinkedStudiesSelector';
import { usePageTitle } from '../../../../contexts/PageTitleContext';
import useLinkedStudiesSelection from '../../../../hooks/features/customer/useLinkedStudiesSelection';
import axiosInstance from '../../../../axiosInstance';

/**
 * RequestDetails - New Request Creation Page
 *
 * Handles both single-form and multi-form submission workflows.
 * Uses MultiFormSubmission component for wizard-style form filling.
 *
 * Supports draft loading when draft_id query parameter is present.
 *
 * Includes RequestAuditTrails tab for UI consistency with MyRequestDetails,
 * though it will show "No audit trails" for new requests.
 *
 * Supports linked studies configuration - when workflow has linked_studies_enabled,
 * shows a Linked Studies tab where user must select at least one study.
 */
const RequestDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const customRequestName = searchParams.get('request_name');
  const [additionalDocumentsComments, setAdditionalDocumentsComments] =
    useState([]);
  const [isValidatingVersion, setIsValidatingVersion] = useState(true);

  // Linked studies selection management
  const {
    eligibleStudies,
    selectedStudyIds,
    setSelectedStudyIds,
    linkedStudiesEnabled,
    loading: linkedStudiesLoading,
    error: linkedStudiesError,
  } = useLinkedStudiesSelection(id);
  const draftId = searchParams.get('draft_id');
  const paymentFailed = searchParams.get('paymentFailed');

  // Set dynamic page title based on request name
  const { setPageTitle, clearPageTitle } = usePageTitle();

  useEffect(() => {
    if (customRequestName) {
      setPageTitle(customRequestName);
    }
    return () => clearPageTitle();
  }, [customRequestName, setPageTitle, clearPageTitle]);

  useEffect(() => {
    if (paymentFailed === 'true') {
      toast.error(t('payment.failedToast'), { position: 'top-center' });
    }
  }, [paymentFailed, t]);

  // Validate that the workflow version is still the latest published one
  useEffect(() => {
    const validateWorkflowVersion = async () => {
      try {
        // Fetch the list of latest/new requests available to the user
        const response = await axiosInstance.get(
          '/api/requests-progress/user/requests/new'
        );
        const latestWorkflowVersions = response?.data || [];

        // Check if the current workflow version ID is in the list of latest versions
        const isValidVersion = latestWorkflowVersions.some(
          (version) => String(version.id) === String(id)
        );

        if (!isValidVersion) {
          toast.error(
            t('workflow.versionOutdatedError') ||
              'This workflow version is no longer active. Redirecting to available workflows.',
            { position: 'top-center' }
          );
          navigate('/customer/requests/new-requests');
        }
      } catch (error) {
        console.error('Error validating workflow version:', error);
        // On error, we still allow the user to proceed (backend will validate)
      } finally {
        setIsValidatingVersion(false);
      }
    };

    validateWorkflowVersion();
  }, [id, navigate, t]);

  // Restore additional documents comments and linked studies from draft
  const handleDraftDataLoaded = useCallback(
    ({
      additionalDocumentsComments: draftComments,
      linkedRequestIds: draftLinkedIds,
    }) => {
      if (draftComments?.length > 0) {
        setAdditionalDocumentsComments(draftComments);
      }
      if (draftLinkedIds?.length > 0) {
        setSelectedStudyIds(draftLinkedIds);
      }
    },
    [setSelectedStudyIds]
  );

  // Show loading while validating workflow version
  if (isValidatingVersion) {
    return (
      <Grid size={12}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <CircularProgress />
        </div>
      </Grid>
    );
  }

  return (
    <div>
      <MultiFormSubmission
        workflowVersionId={id}
        customRequestName={customRequestName}
        isEditable={true}
        showValidationIndicators={true}
        requestAuditTrailsSlot={<RequestAuditTrails requestId={null} />}
        additionalDocumentsComments={additionalDocumentsComments}
        additionalDocumentsSlot={
          <AdditionalDocuments
            requestId={null}
            requestDateEnded={null}
            additionalDocumentsComments={additionalDocumentsComments}
            setAdditionalDocumentsComments={setAdditionalDocumentsComments}
          />
        }
        linkedStudiesSelectorSlot={
          linkedStudiesEnabled && (
            <LinkedStudiesSelector
              eligibleStudies={eligibleStudies}
              selectedStudyIds={selectedStudyIds}
              onSelectionChange={setSelectedStudyIds}
              loading={linkedStudiesLoading}
              error={linkedStudiesError}
            />
          )
        }
        linkedRequestIds={selectedStudyIds}
        draftId={draftId ? parseInt(draftId, 10) : null}
        onDraftDataLoaded={handleDraftDataLoaded}
      />
    </div>
  );
};

export default RequestDetails;
